import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useLang } from "../LangContext";
import { LOGO_URL } from "../constants";

const NavLink = ({ href, label, onClick, testId }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={testId}
    className="text-sm font-medium tracking-wide text-[#1e3a5f]/80 hover:text-[#7a8b50] transition-colors"
  >
    {label}
  </a>
);

export default function Header() {
  const { lang, toggle, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isLanding = location.pathname === "/";

  const scrollTo = (id) => {
    setOpen(false);
    if (!isLanding) {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-[#FAFAF5]/90 backdrop-blur-md border-b border-[#1e3a5f]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
          <img
            src={LOGO_URL}
            alt="Ruizcare"
            className="h-12 w-12 object-contain"
          />
          <div className="leading-tight">
            <div className="font-serif-display text-[22px] text-[#1e3a5f] font-semibold tracking-tight">
              Ruizcare
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#7a8b50] font-semibold">
              Servicios del Hogar
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          <NavLink testId="nav-services" href="#servicios" onClick={(e) => { e.preventDefault(); scrollTo("servicios"); }} label={t.nav.services} />
          <NavLink testId="nav-about" href="#sobre" onClick={(e) => { e.preventDefault(); scrollTo("sobre"); }} label={t.nav.about} />
          <NavLink testId="nav-booking" href="#reserva" onClick={(e) => { e.preventDefault(); scrollTo("reserva"); }} label={t.nav.booking} />
          <NavLink testId="nav-contact" href="#contacto" onClick={(e) => { e.preventDefault(); scrollTo("contacto"); }} label={t.nav.contact} />
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold tracking-widest uppercase border border-[#1e3a5f]/20 rounded-full px-3 py-1.5 hover:border-[#7a8b50] hover:text-[#7a8b50] transition-colors"
            aria-label="Cambiar idioma"
          >
            <span className={lang === "es" ? "text-[#1e3a5f]" : "text-[#1e3a5f]/40"}>ES</span>
            <span className="text-[#1e3a5f]/30">/</span>
            <span className={lang === "eu" ? "text-[#1e3a5f]" : "text-[#1e3a5f]/40"}>EU</span>
          </button>

          <Link
            to="/admin/login"
            data-testid="header-admin-link"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold tracking-widest uppercase text-[#1e3a5f]/70 hover:text-[#7a8b50]"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> {t.nav.admin}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden p-2 rounded-full hover:bg-[#1e3a5f]/5"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#1e3a5f]/10 bg-[#FAFAF5]" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            <button className="text-left text-base font-medium text-[#1e3a5f]" onClick={() => scrollTo("servicios")} data-testid="mnav-services">{t.nav.services}</button>
            <button className="text-left text-base font-medium text-[#1e3a5f]" onClick={() => scrollTo("sobre")} data-testid="mnav-about">{t.nav.about}</button>
            <button className="text-left text-base font-medium text-[#1e3a5f]" onClick={() => scrollTo("reserva")} data-testid="mnav-booking">{t.nav.booking}</button>
            <button className="text-left text-base font-medium text-[#1e3a5f]" onClick={() => scrollTo("contacto")} data-testid="mnav-contact">{t.nav.contact}</button>
            <div className="flex items-center gap-3 pt-3 border-t border-[#1e3a5f]/10">
              <button data-testid="mlang-toggle" onClick={toggle} className="text-xs font-semibold tracking-widest uppercase border border-[#1e3a5f]/20 rounded-full px-3 py-1.5">
                {lang.toUpperCase()}
              </button>
              <Link data-testid="mnav-admin" to="/admin/login" className="text-xs font-semibold tracking-widest uppercase text-[#1e3a5f]/70">
                {t.nav.admin}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
